import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { Loader2, Send, GraduationCap, BookOpen, FileText, Sparkles, BookmarkPlus, Copy, RefreshCw, Mic, Bold, Italic, Code, Calculator, X, Plus, ArrowLeft, Radar, Target, Waves, Filter, BarChart2, Gauge, Compass, Radio, Satellite, Clock, Cpu, Shield, Network, Zap, Crosshair, LineChart, PieChart, TrendingUp, Activity, Signal, Wifi, Broadcast, Box, Circle, Move, Navigation, Map, Image as ImageIcon, Trash2, ChevronsDown } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';  // Import the custom hook
import { useTheme } from '../contexts/ThemeContext';
import marked from 'marked';

const TypewriterText = ({ content, onComplete }) => {
    const [displayedContent, setDisplayedContent] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const isNewMessageRef = useRef(false);
    const initialContentRef = useRef('');
    const { theme } = useTheme();
    
    useEffect(() => {
        // If content changes completely, it's likely a new message
        if (content !== initialContentRef.current) {
            initialContentRef.current = content;
            isNewMessageRef.current = true;
            setCurrentIndex(0);
            setDisplayedContent('');
        }
    }, [content]);
    
    // Function to determine chunk size based on context - adds variance
    const getChunkSize = (text, index) => {
        // If we're at a space, consider word boundaries
        if (text[index] === ' ' && Math.random() > 0.5) {
            // Find the end of the next word (or next few words)
            const remainingText = text.substring(index + 1);
            let wordCount = Math.floor(Math.random() * 3) + 1; // 1-3 words at once
            let endPos = 0;
            
            while (wordCount > 0 && endPos < remainingText.length) {
                // Find next space
                const nextSpace = remainingText.indexOf(' ', endPos);
                if (nextSpace === -1) {
                    // No more spaces, go to end of text
                    endPos = remainingText.length;
                    break;
                } else {
                    endPos = nextSpace + 1;
                    wordCount--;
                }
            }
            
            return endPos + 1; // +1 for the initial space
        }
        
        // Special handling for code blocks and punctuation
        if (text.substring(index, index + 3) === '```' || 
            text.substring(index, index + 3) === '---' ||
            ',.!?:;)]}"\'>'.includes(text[index])) {
            return 1;
        }
        
        // Regular case: 1-5 characters at once with randomized probability
        return Math.floor(Math.random() * 5) + 1;
    };
    
    useEffect(() => {
        // For existing messages loaded from history, show them immediately
        if (content && currentIndex === 0 && !isNewMessageRef.current) {
            setDisplayedContent(content);
            setCurrentIndex(content.length);
            if (onComplete) onComplete();
            return;
        }
        
        // For new messages, animate them with variable speed and chunk size
        if (content && currentIndex < content.length && isNewMessageRef.current) {
            const chunkSize = getChunkSize(content, currentIndex);
            const nextIndex = Math.min(currentIndex + chunkSize, content.length);
            
            // Vary the typing speed based on punctuation and context
            let typingSpeed = 5; // Base speed (much faster than before)
            
            // Slow down slightly at sentence endings
            if ('.!?'.includes(content[currentIndex - 1]) && content[currentIndex] === ' ') {
                typingSpeed = 20;
            }
            
            const timeout = setTimeout(() => {
                setDisplayedContent(content.substring(0, nextIndex));
                setCurrentIndex(nextIndex);
            }, typingSpeed);
            
            return () => clearTimeout(timeout);
        } else if (currentIndex >= content.length && content && isNewMessageRef.current) {
            if (onComplete) onComplete();
        }
    }, [content, currentIndex, onComplete]);
    
    return (
        <div style={{ color: theme.text }}>
            <ReactMarkdown
                remarkPlugins={[remarkMath]}
                rehypePlugins={[rehypeKatex]}
                className="prose max-w-none prose-sm sm:prose-base"
                components={{
                    p: ({children, ...props}) => <p style={{color: theme.text}} className="mb-4 last:mb-0 text-sm sm:text-base" {...props}>{children}</p>,
                    h1: ({children, ...props}) => <h1 style={{color: theme.text}} className="text-lg sm:text-xl mb-2" {...props}>{children}</h1>,
                    h2: ({children, ...props}) => <h2 style={{color: theme.text}} className="text-base sm:text-lg mb-2" {...props}>{children}</h2>,
                    h3: ({children, ...props}) => <h3 style={{color: theme.text}} className="text-base mb-2" {...props}>{children}</h3>,
                    li: ({children, ...props}) => <li style={{color: theme.text}} className="text-sm sm:text-base" {...props}>{children}</li>,
                    strong: ({children, ...props}) => <strong style={{color: theme.text}} {...props}>{children}</strong>,
                    em: ({children, ...props}) => <em style={{color: theme.text}} {...props}>{children}</em>,
                    code: ({children, ...props}) => <code style={{
                        backgroundColor: 'rgba(0,0,0,0.05)', 
                        padding: '2px 4px', 
                        borderRadius: '3px', 
                        color: theme.text
                    }} {...props}>{children}</code>
                }}
            >
                {displayedContent}
            </ReactMarkdown>
        </div>
    );
};

const MessageActions = ({ message, regenerateResponse }) => {
    const { theme } = useTheme();
    
    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Copied to clipboard');
        } catch (error) {
            toast.error('Failed to copy text');
        }
    };

    const saveToNotes = async (content) => {
        try {
            // Implement save to notes functionality
            toast.success('Saved to notes');
        } catch (error) {
            toast.error('Failed to save notes');
        }
    };

    return (
        <div 
            className="flex gap-1.5 mt-2 justify-end opacity-0 group-hover:opacity-100 transition-all duration-200"
        >
            <button 
                onClick={() => copyToClipboard(message.content)}
                className="p-1.5 rounded-md hover:bg-opacity-90 transition-colors flex items-center justify-center"
                style={{ 
                    color: theme.accent,
                    background: `${theme.secondary}90`,
                    border: `1px solid ${theme.border}`,
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
                title="Copy to clipboard"
                aria-label="Copy to clipboard"
            >
                <Copy size={14} />
            </button>
            <button 
                onClick={() => saveToNotes(message.content)}
                className="p-1.5 rounded-md hover:bg-opacity-90 transition-colors flex items-center justify-center"
                style={{ 
                    color: theme.accent,
                    background: `${theme.secondary}90`,
                    border: `1px solid ${theme.border}`,
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
                title="Save to notes"
                aria-label="Save to notes"
            >
                <BookmarkPlus size={14} />
            </button>
            {message.role === 'assistant' && (
                <button 
                    onClick={() => regenerateResponse(message)}
                    className="p-1.5 rounded-md hover:bg-opacity-90 transition-colors flex items-center justify-center"
                    style={{ 
                        color: theme.text,
                        background: `${theme.secondary}90`,
                        border: `1px solid ${theme.border}`,
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    title="Regenerate response"
                    aria-label="Regenerate response"
                >
                    <RefreshCw size={14} />
                </button>
            )}
        </div>
    );
};

const ICON_MAP = {
    'filter': Filter,
    'clock': Clock,
    'network': Network,
    'signal': Signal,
    'gauge': Gauge,
    'target': Target,
    'shield': Shield,
    'wave': Waves,
    'chart': BarChart2,
    'file': FileText
};

const PromptCard = ({ prompt, onClick }) => {
    const { theme } = useTheme();
    
    const getIconComponent = (iconName) => {
        if (typeof iconName !== 'string') {
            return FileText;
        }
        
        const normalizedName = iconName.toLowerCase();
        return ICON_MAP[normalizedName] || FileText;
    };
    
    const IconComponent = getIconComponent(prompt.icon);
    
    return (
        <button 
            onClick={onClick}
            data-icon={prompt.icon}
            className="flex items-start gap-2 sm:gap-5 p-3 sm:p-7 rounded-2xl text-left transition-all duration-300 cursor-pointer relative overflow-hidden border hover:transform hover:-translate-y-0.5"
            style={{ 
                background: theme.background,
                borderColor: theme.border,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.02)'
            }}
        >
            <div className="p-2 sm:p-4 rounded-xl flex items-center justify-center transition-all duration-300"
                style={{ background: theme.primary }}
            >
                <IconComponent size={20} style={{ color: theme.text }} />
            </div>
            <div className="flex-1">
                <h3 className="font-semibold mb-1 sm:mb-3 text-base sm:text-lg leading-tight tracking-tight"
                     style={{ color: theme.text }}
                >
                    {prompt.title}
                </h3>
                <p className="text-xs sm:text-sm leading-relaxed"
                   style={{ color: theme.accent }}
                >
                    {prompt.description}
                </p>
            </div>
        </button>
    );
};

const ImagePreview = ({ imageFile, onRemove, theme, size }) => {
    const [preview, setPreview] = useState(null);
    
    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' bytes';
        else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    useEffect(() => {
        if (!imageFile) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            setPreview(reader.result);
        };
        reader.readAsDataURL(imageFile);

        return () => {
            reader.abort();
        };
    }, [imageFile]);

    if (!preview) return null;

    return (
        <div className="relative group">
            <div className="border rounded-lg overflow-hidden" style={{ borderColor: theme.border }}>
                <img 
                    src={preview} 
                    alt="Preview" 
                    className="h-32 object-contain"
                />
                <div 
                    className="text-xs px-2 py-1 truncate"
                    style={{ color: theme.accent, background: theme.secondary }}
                >
                    {imageFile.name} ({formatFileSize(size || imageFile.size)})
                </div>
            </div>
            <button
                onClick={onRemove}
                className="absolute top-1 right-1 bg-opacity-70 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ 
                    backgroundColor: `${theme.secondary}CC`,
                    color: theme.accent
                }}
            >
                <Trash2 size={16} />
            </button>
        </div>
    );
};

const ContextSuggestions = ({ suggestions, onSuggestionClick, theme }) => {
    if (!suggestions || suggestions.length === 0) return null;

    return (
        <div className="flex flex-wrap justify-center gap-2 mt-3 mb-5 px-3">
            {suggestions.map((suggestion, index) => {
                const IconComponent = ICON_MAP[suggestion.icon] || FileText;
                
                return (
                    <button
                        key={index}
                        onClick={() => onSuggestionClick(suggestion.message)}
                        className="flex items-center gap-1.5 sm:gap-2 py-2 sm:py-1.5 px-3 sm:px-3 rounded-full text-sm border transition-all hover:transform hover:scale-105"
                        style={{
                            backgroundColor: `${theme.secondary}80`,
                            borderColor: theme.border,
                            color: theme.text
                        }}
                    >
                        <IconComponent size={14} style={{ color: theme.accent }} />
                        <span className="truncate max-w-[200px] sm:max-w-[200px]">{suggestion.message}</span>
                    </button>
                );
            })}
        </div>
    );
};

const LectureChat = () => {
    const { theme } = useTheme();
    const { isLoaded, isSignedIn } = useAuth();
    const authFetch = useAuthenticatedFetch();
    
    // Use ref for the authFetch function to maintain stable identity
    const authFetchRef = useRef(authFetch);
    
    // Update the ref only when authFetch changes
    useEffect(() => {
        authFetchRef.current = authFetch;
    }, [authFetch]);
    
    // Properly memoize the fetch function to avoid it recreating on every render
    const authenticatedFetch = useCallback(async (url, options = {}) => {
        return authFetchRef.current(url, options);
    }, []); // No dependencies ensures stable reference
    
    const { classId, lectureId, chatId } = useParams();
    const navigate = useNavigate();
    const [lecture, setLecture] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    const [showPrompts, setShowPrompts] = useState(true);
    const chatContainerRef = useRef(null);
    const textareaRef = useRef(null);
    const [threads, setThreads] = useState([
        { id: 'default', title: 'Main Thread', messages: [] }
    ]);
    const threadsRef = useRef(threads);
    const [activeThread, setActiveThread] = useState('default');
    const [isRecording, setIsRecording] = useState(false);
    const [suggestedPrompts, setSuggestedPrompts] = useState([]);
    const [showFormatting, setShowFormatting] = useState(false);
    const [completedMessages, setCompletedMessages] = useState({});
    const [messageIsComplete, setMessageIsComplete] = useState({});
    const [contextSuggestions, setContextSuggestions] = useState([]);
    const [imageFiles, setImageFiles] = useState([]);
    const [compressedImageData, setCompressedImageData] = useState([]);
    const imageInputRef = useRef(null);
    const [isProcessingImages, setIsProcessingImages] = useState(false);
    const userHasScrolled = useRef(false);
    const shouldScrollToBottom = useRef(true);
    const prevMessageCount = useRef(0);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showSidebar, setShowSidebar] = useState(false);
    const [chatContext, setChatContext] = useState(null);
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [isSending, setIsSending] = useState(false);
    
    // Track state changes
    const prevSessionId = useRef(null);
    
    // Modify TypewriterText component usage to prevent re-animations on re-renders
    const TypewriterKey = useRef({});
    const lastMessageRef = useRef(null);

    // Update threadsRef whenever threads changes
    useEffect(() => {
        threadsRef.current = threads;
    }, [threads]);
    
    // Reset key for fresh animation when receiving new messages
    useEffect(() => {
        const currentThread = threads.find(t => t.id === activeThread);
        if (currentThread?.messages) {
            const lastMessage = currentThread.messages[currentThread.messages.length - 1];
            
            if (lastMessage && lastMessage !== lastMessageRef.current && lastMessage.role === 'assistant') {
                TypewriterKey.current[`${activeThread}-${currentThread.messages.length - 1}`] = Date.now();
                lastMessageRef.current = lastMessage;
            }
        }
    }, [threads, activeThread]);

    useEffect(() => {
        document.documentElement.style.setProperty('--background', theme.background);
        document.documentElement.style.setProperty('--text', theme.text);
        document.documentElement.style.setProperty('--border', theme.border);
        document.documentElement.style.setProperty('--primary', theme.primary);
        document.documentElement.style.setProperty('--secondary', theme.secondary);
        document.documentElement.style.setProperty('--accent', theme.accent);
        document.documentElement.style.setProperty('--hover', theme.primaryHover);
    }, [theme]);

    useEffect(() => {
        let isMounted = true;

        const fetchContextData = async () => {
            if (!isLoaded || !isSignedIn) return;

            try {
                setIsLoading(true);
                
                // Check if we have a context from navigation state
                const contextFromNav = location.state?.context;
                
                if (contextFromNav) {
                    if (contextFromNav.type === 'lectures') {
                        // Multiple lectures context
                        const lectures = contextFromNav.items;
                        
                        // Get the class ID from the first lecture (all should be from the same class)
                        const classId = lectures[0]?.class_id || lectures[0]?.classId;
                        
                        const transcripts = await Promise.all(
                            lectures.map(async (lecture) => {
                                // Use the proper lecture ID and class ID from the lecture object
                                const lectureId = lecture.id;
                                const response = await authenticatedFetch(
                                    `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/${classId}/${lectureId}`
                                );
                                const data = await response.json();
                                return data.transcript;
                            })
                        );
                        
                        setChatContext({
                            type: 'lectures',
                            lectures: lectures,
                            transcripts: transcripts
                        });
                    } else if (contextFromNav.type === 'class') {
                        // Entire class context
                        const classData = contextFromNav.item;
                        const response = await authenticatedFetch(
                            `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/${classData.id}`
                        );
                        const lectures = await response.json();
                        
                        const transcripts = await Promise.all(
                            lectures.map(async (lecture) => {
                                const lectureResponse = await authenticatedFetch(
                                    `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/${classData.id}/${lecture.id}`
                                );
                                const data = await lectureResponse.json();
                                return data.transcript;
                            })
                        );
                        
                        setChatContext({
                            type: 'class',
                            class: classData,
                            lectures: lectures,
                            transcripts: transcripts
                        });
                    }
                } else if (lectureId) {
                    // Single lecture context (existing functionality)
                    const response = await authenticatedFetch(
                        `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/${classId}/${lectureId}`
                    );
                    const data = await response.json();
                    setLecture(data);
                    setChatContext({
                        type: 'lecture',
                        lecture: data,
                        transcript: data.transcript
                    });
                }
            } catch (error) {
                toast.error('Failed to load chat context');
            } finally {
                setIsLoading(false);
            }
        };

        fetchContextData();

        return () => {
            isMounted = false;
        };
    }, [classId, lectureId, isLoaded, isSignedIn, location.state, authenticatedFetch]);

    useEffect(() => {
        let isMounted = true;

        const fetchLecture = async () => {
            if (!isLoaded || !isSignedIn) {
                return;
            }

            try {
                const response = await authenticatedFetch(
                    `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/${classId}/${lectureId}`
                );
                
                if (!isMounted) return;

                const data = await response.json();
                setLecture(data);
                
                // Generate suggestions after fetching lecture
                if (data.transcript) {
                    const suggestionsResponse = await authenticatedFetch(
                        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/generate-suggestions',
                        {
                            method: 'POST',
                            body: JSON.stringify({ 
                                transcript: data.transcript,
                                title: data.title,
                                description: data.description,
                                subject: data.subject
                            }),
                        }
                    );
                    
                    if (!isMounted) return;
                    
                    const suggestions = await suggestionsResponse.json();
                    setSuggestedPrompts(suggestions);
                }
            } catch (error) {
                toast.error('Failed to load lecture');
            }
        };

        fetchLecture();

        return () => {
            isMounted = false;
        };
    }, [classId, lectureId, isLoaded, isSignedIn, authenticatedFetch]);

    useEffect(() => {
        if (!chatContainerRef.current) return;
        
        const currentThread = threads.find(t => t.id === activeThread);
        const currentMessages = currentThread?.messages || [];
        
        // Check if new messages were added
        const newMessageAdded = currentMessages.length > prevMessageCount.current;
        prevMessageCount.current = currentMessages.length;
        
        // Is the last message from the AI? If so, we want to scroll to it
        const lastMessageIsAI = 
            currentMessages.length > 0 && 
            currentMessages[currentMessages.length - 1]?.role === 'assistant';
        
        // Auto-scroll if:
        // 1. A new message was added AND
        // 2. Either:
        //    a. User hasn't scrolled away (shouldScrollToBottom is true) OR
        //    b. User sent the message (last message is AI response) OR
        //    c. We're in thinking state (showing the typing indicator)
        if (newMessageAdded && (shouldScrollToBottom.current || lastMessageIsAI || isThinking)) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages, threads, activeThread, isThinking, completedMessages]);
    
    const handleChatScroll = useCallback(() => {
        if (!chatContainerRef.current) return;
        
        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
        // Consider "close to bottom" as within 100px of the bottom
        const isCloseToBottom = scrollHeight - scrollTop - clientHeight < 100;
        
        // If we're close to the bottom, we should auto-scroll on new messages
        shouldScrollToBottom.current = isCloseToBottom;
        
        // Show scroll button only when we're not at the bottom
        setShowScrollButton(!isCloseToBottom);
        
        // If user has scrolled up (not close to bottom), mark that they've manually scrolled
        if (!isCloseToBottom) {
            userHasScrolled.current = true;
        }
    }, []);
    
    useEffect(() => {
        const chatContainer = chatContainerRef.current;
        if (chatContainer) {
            chatContainer.addEventListener('scroll', handleChatScroll);
            return () => chatContainer.removeEventListener('scroll', handleChatScroll);
        }
    }, [handleChatScroll]);
    
    const handleMessageComplete = useCallback((messageId) => {
        setCompletedMessages(prev => {
            if (prev[messageId]) return prev; // Already marked as complete
            return {
                ...prev,
                [messageId]: true
            };
        });
        
        // After a message completes typing, scroll to bottom
        if (chatContainerRef.current && shouldScrollToBottom.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, []);

    const scrollToBottom = useCallback(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            shouldScrollToBottom.current = true;
        }
    }, []);

    const handleImageAttach = async (e) => {
        if (!e.target.files || e.target.files.length === 0) return;
        
        toast.loading('Processing images...', { id: 'image-processing' });
        
        const newFiles = Array.from(e.target.files);
        
        // Validate file types - only accept images
        const validFileTypes = newFiles.filter(file => 
            file.type.startsWith('image/') && ['image/jpeg', 'image/png', 'image/webp'].includes(file.type)
        );
        
        if (validFileTypes.length !== newFiles.length) {
            toast.error('Only JPEG, PNG, and WebP images are supported');
        }
        
        // Validate file sizes - max 10MB per file for original (we'll compress them)
        const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
        const validFileSizes = validFileTypes.filter(file => file.size <= MAX_FILE_SIZE);
        
        if (validFileSizes.length !== validFileTypes.length) {
            toast.error('Images must be smaller than 10MB');
        }

        if (validFileSizes.length === 0) {
            toast.dismiss('image-processing');
            return;
        }
        
        try {
            setIsProcessingImages(true);
            
            // Process each valid image safely
            const processedImages = await Promise.all(
                validFileSizes.map(async (file) => {
                    try {
                        return new Promise((resolve) => {
                            // Create an image element to get dimensions
                            const img = new Image();
                            
                            img.onload = () => {
                                try {
                                    // Calculate new dimensions
                                    const MAX_DIMENSION = 1200;
                                    let width = img.width;
                                    let height = img.height;
                                    
                                    if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                                        if (width > height) {
                                            height = Math.round(height * (MAX_DIMENSION / width));
                                            width = MAX_DIMENSION;
                                        } else {
                                            width = Math.round(width * (MAX_DIMENSION / height));
                                            height = MAX_DIMENSION;
                                        }
                                    }
                                    
                                    // Create a canvas to resize the image
                                    const canvas = document.createElement('canvas');
                                    canvas.width = width;
                                    canvas.height = height;
                                    
                                    const ctx = canvas.getContext('2d');
                                    ctx.drawImage(img, 0, 0, width, height);
                                    
                                    // Convert to blob with quality reduction for JPEGs
                                    const QUALITY = 0.7; // 70% quality
                                    
                                    if (canvas.toBlob) {
                                        canvas.toBlob(
                                            (blob) => {
                                                // Clean up object URL
                                                URL.revokeObjectURL(img.src);
                                                
                                                if (!blob) {
                                                    resolve(null);
                                                    return;
                                                }
                                                
                                                // Convert compressed blob to base64
                                                const reader = new FileReader();
                                                reader.onloadend = () => {
                                                    resolve({
                                                        file,
                                                        dataUrl: reader.result,
                                                        compressedSize: blob.size
                                                    });
                                                };
                                                reader.onerror = () => {
                                                    resolve(null);
                                                };
                                                reader.readAsDataURL(blob);
                                            },
                                            file.type,
                                            QUALITY
                                        );
                                    } else {
                                        // Fallback for browsers without toBlob support
                                        const dataURL = canvas.toDataURL(file.type, QUALITY);
                                        resolve({
                                            file,
                                            dataUrl: dataURL,
                                            compressedSize: Math.round(dataURL.length * 0.75) // Rough estimate
                                        });
                                    }
                                } catch (err) {
                                    resolve(null);
                                }
                            };
                            
                            img.onerror = () => {
                                URL.revokeObjectURL(img.src);
                                resolve(null);
                            };
                            
                            // Use a try-catch here too in case createObjectURL fails
                            try {
                                img.src = URL.createObjectURL(file);
                            } catch (err) {
                                resolve(null);
                            }
                        });
                    } catch (error) {
                        return null;
                    }
                })
            );
            
            // Filter out any failed processing
            const successfullyProcessed = processedImages.filter(Boolean);
            
            if (successfullyProcessed.length === 0) {
                toast.error('Failed to process images');
                toast.dismiss('image-processing');
                return;
            }
            
            // Add processed images to state
            setImageFiles(prev => [...prev, ...successfullyProcessed.map(p => p.file)]);
            setCompressedImageData(prev => [...prev, ...successfullyProcessed]);
            
            // Calculate compression stats for toast
            const originalSize = successfullyProcessed.reduce((acc, img) => acc + img.file.size, 0);
            const compressedSize = successfullyProcessed.reduce((acc, img) => acc + img.compressedSize, 0);
            const savingsPercent = Math.round((1 - compressedSize / originalSize) * 100);
            
            toast.success(
                `Processed ${successfullyProcessed.length} image${successfullyProcessed.length > 1 ? 's' : ''} (${savingsPercent}% smaller)`,
                { id: 'image-processing' }
            );
        } catch (error) {
            toast.error('Failed to process images', { id: 'image-processing' });
        } finally {
            if (e.target) e.target.value = null;
            setIsProcessingImages(false);
        }
    };
    
    const removeImageAttachment = (index) => {
        setImageFiles(prev => prev.filter((_, i) => i !== index));
        setCompressedImageData(prev => prev.filter((_, i) => i !== index));
    };

    // Store sessionId in a ref to avoid dependency changes
    const sessionIdRef = useRef(null);
    useEffect(() => {
        sessionIdRef.current = sessionId;
    }, [sessionId]);
    
    // Replace the polling effect with a single fetch when needed
    const fetchMessagesOnce = useCallback(async () => {
        const currentSessionId = sessionIdRef.current;
        if (!currentSessionId) return;
        
        try {
            const response = await authenticatedFetch(
                `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/chat/sessions/${currentSessionId}/messages`
            );

            if (!response.ok) {
                throw new Error('Failed to fetch messages');
            }

            const data = await response.json();
            
            if (data && data.messages) {
                // Only update state if we have new messages
                setThreads([{
                    id: 'default',
                    title: data.session?.title || 'Chat Session',
                    messages: data.messages
                }]);
            }
        } catch (error) {
        }
    }, [authenticatedFetch]); // No sessionId dependency
    
    // Use a ref to track if we've already fetched messages for this session
    const hasInitiallyFetched = useRef({});
    
    // Only fetch messages once when the sessionId changes
    useEffect(() => {
        if (sessionId && !hasInitiallyFetched.current[sessionId]) {
            fetchMessagesOnce();
            hasInitiallyFetched.current[sessionId] = true;
        }
    }, [sessionId, fetchMessagesOnce]);

    const sendMessage = async (content) => {
        if (!content.trim() || !chatContext) return;
        
        setIsSending(true);
        setIsThinking(true);
        
        const userMessage = { 
            role: 'user', 
            content,
            images: compressedImageData.length > 0 
                ? compressedImageData.map(img => img.dataUrl) 
                : undefined
        };

        try {
            let currentSessionId = sessionId;
            
            // If no session exists, create one first
            if (!currentSessionId) {
                const sessionResponse = await authenticatedFetch(
                    'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/chat/sessions',
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            lecture_id: lectureId,
                            class_id: classId,
                            title: lecture?.title || 'Untitled Chat',
                            context_type: chatContext.type,
                            context_data: chatContext
                        }),
                    }
                );

                if (!sessionResponse.ok) {
                    throw new Error('Failed to create chat session');
                }

                const sessionData = await sessionResponse.json();
                currentSessionId = sessionData.id;
                setSessionId(currentSessionId);
                navigate(`/chat/${currentSessionId}`);
            }

            // Save user message
            await authenticatedFetch(
                `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/chat/sessions/${currentSessionId}/messages`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(userMessage),
                }
            );

            // Update local state with user message
            setThreads(prevThreads => 
                prevThreads.map(thread => 
                    thread.id === activeThread
                        ? { ...thread, messages: [...thread.messages, userMessage] }
                        : thread
                )
            );

            // Get AI response
            const response = await authenticatedFetch(
                'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/chat',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        message: content,
                        context: chatContext,
                        chatHistory: threads.find(t => t.id === activeThread)?.messages || [],
                        images: compressedImageData.map(img => img.dataUrl),
                        sessionId: currentSessionId
                    }),
                }
            );

            if (!response.ok) {
                throw new Error('Failed to get AI response');
            }

            const data = await response.json();
            const aiMessage = { role: 'assistant', content: data.response };

            // Save AI message
            await authenticatedFetch(
                `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/chat/sessions/${currentSessionId}/messages`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(aiMessage),
                }
            );

            // Update local state with AI message
            setThreads(prevThreads => 
                prevThreads.map(thread => 
                    thread.id === activeThread
                        ? { ...thread, messages: [...thread.messages, aiMessage] }
                        : thread
                )
            );

            // Clear input and reset states
            setInput('');
            setShowPrompts(false);
            setImageFiles([]);
            setCompressedImageData([]);
            setContextSuggestions([]);

        } catch (error) {
            toast.error('Failed to send message: ' + error.message);
        } finally {
            setIsSending(false);
            setIsThinking(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        sendMessage(input);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const adjustTextareaHeight = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    };

    const handlePromptClick = (prompt) => {
        sendMessage(prompt);
    };

    const startVoiceInput = async () => {
        try {
            // Check if the API exists before trying to use it
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            if (!SpeechRecognition) {
                throw new Error('Speech recognition not supported in this browser');
            }
            
            // Create instance safely - explicitly make it a constructor call
            const recognition = new SpeechRecognition();
            recognition.continuous = false;
            recognition.interimResults = false;
            
            recognition.onresult = (event) => {
                if (event.results && event.results[0] && event.results[0][0]) {
                    const transcript = event.results[0][0].transcript;
                    setInput(transcript);
                }
                setIsRecording(false);
            };
            
            recognition.onerror = (event) => {
                setIsRecording(false);
                toast.error(`Speech recognition error: ${event.error}`);
            };
            
            recognition.onend = () => {
                setIsRecording(false);
            };
            
            recognition.start();
            setIsRecording(true);
        } catch (error) {
            toast.error('Speech recognition is not supported in your browser');
            setIsRecording(false);
        }
    };

    const deleteThread = (threadId) => {
        if (threadId === 'default') return;
        
        setThreads(prevThreads => prevThreads.filter(thread => thread.id !== threadId));
        if (activeThread === threadId) {
            setActiveThread('default');
        }
    };

    const createNewThread = () => {
        const newThread = {
            id: `thread-${Date.now()}`,
            title: `Thread ${threads.length + 1}`,
            messages: []
        };
        setThreads(prevThreads => [...prevThreads, newThread]);
        setActiveThread(newThread.id);
    };

    const updateThreadTitle = (threadId, newTitle) => {
        setThreads(prevThreads => 
            prevThreads.map(thread => 
                thread.id === threadId 
                    ? { ...thread, title: newTitle }
                    : thread
            )
        );
    };

    const saveToNotes = async (message) => {
        try {
            // You'll need to implement the API endpoint for this
        } catch (error) {
        }
    };

    const copyToClipboard = async (content) => {
        try {
        } catch (error) {
        }
    };

    const regenerateResponse = async (message) => {
        if (!lecture?.transcript || isThinking) return;

        setIsThinking(true);
        try {
            const response = await authenticatedFetch(
                'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/chat',
                {
                    method: 'POST',
                    body: JSON.stringify({
                        message: "Please regenerate your last response with more detail",
                        transcript: lecture.transcript,
                        chatHistory: threads.find(t => t.id === activeThread)?.messages.slice(0, -1),
                    }),
                }
            );

            const data = await response.json();
            
            setThreads(prevThreads => {
                return prevThreads.map(thread => {
                    if (thread.id === activeThread) {
                        const messages = [...thread.messages];
                        for (let i = messages.length - 1; i >= 0; i--) {
                            if (messages[i].role === 'assistant') {
                                messages[i] = {
                                    role: 'assistant',
                                    content: data.response
                                };
                                break;
                            }
                        }
                        return { ...thread, messages };
                    }
                    return thread;
                });
            });
        } catch (error) {
            toast.error('Failed to regenerate response');
        } finally {
            setIsThinking(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setInput(suggestion);
        setContextSuggestions([]);
        textareaRef.current?.focus();
    };

    const loadContextSuggestions = async () => {
        if (!lecture?.transcript) return;
        
        try {
            const activeThreadMessages = threads.find(t => t.id === activeThread)?.messages || [];
            
            // Only get suggestions if there are messages and the last message is from the assistant
            if (activeThreadMessages.length < 2 || 
                activeThreadMessages[activeThreadMessages.length - 1].role !== 'assistant') {
                return;
            }
            
            // Get the last few messages for better context
            const recentMessages = activeThreadMessages.slice(-4);
            
            const response = await authenticatedFetch(
                'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/generate-suggestions',
                {
                    method: 'POST',
                    body: JSON.stringify({
                        transcript: lecture.transcript,
                        chatHistory: recentMessages
                    }),
                }
            );

            const suggestions = await response.json();
            setContextSuggestions(suggestions);
        } catch (error) {
        }
    };

    // Add a resize listener to detect mobile/desktop
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) {
                setShowSidebar(false); // Reset sidebar state on desktop
            }
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Toggle sidebar visibility
    const toggleSidebar = () => {
        setShowSidebar(!showSidebar);
    };

    // Close sidebar when a thread is selected on mobile
    const handleThreadSelect = (threadId) => {
        setActiveThread(threadId);
        if (isMobile) {
            setShowSidebar(false);
        }
    };

    // Add this new function near the other handler functions
    const deleteChat = async () => {
        if (!sessionId) return;
        
        try {
            const response = await authenticatedFetch(
                `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/chat/sessions/${sessionId}`,
                {
                    method: 'DELETE'
                }
            );

            // Navigate based on context
            if (classId && lectureId) {
                // If we have classId and lectureId, navigate back to the lecture page
                navigate(`/class/${classId}/lecture/${lectureId}`);
            } else {
                // Otherwise, navigate back to the ChatHub
                navigate('/chat');
            }
            
            toast.success('Chat deleted successfully');
        } catch (error) {
            toast.error('Failed to delete chat');
        }
    };

    if (!isSignedIn) {
        navigate('/sign-in');
        return null;
    }

    if (!isLoaded || !lecture) {
        return (
            <div className="h-screen flex items-center justify-center" style={{ background: theme.background }}>
                <div className="text-center">
                    <Loader2 
                        size={60} 
                        className="animate-spin" 
                        style={{ color: theme.accent }} 
                    />
                    <p className="mt-4" style={{ marginTop: '1rem', color: theme.text, fontWeight: 500 }}>Loading lecture...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen overflow-hidden" style={{ background: theme.background }}>
            {/* Header - visible at top */}
            <header className="z-30 border-b h-14 sm:h-16 px-3 sm:px-6 flex items-center justify-between sticky top-0" 
                style={{ 
                    borderColor: theme.border,
                    background: theme.background
                }}
            >
                <div className="flex items-center gap-2 sm:gap-4">
                    <button 
                        onClick={() => {
                            // If we have classId and lectureId in the URL, go back to that lecture
                            if (classId && lectureId) {
                                navigate(`/class/${classId}/lecture/${lectureId}`);
                            } else {
                                // Otherwise, go back to the ChatHub
                                navigate('/chat');
                            }
                        }}
                        className="flex items-center gap-1 sm:gap-2 py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg transition-colors"
                        style={{ color: theme.accent }}
                    >
                        <ArrowLeft size={18} />
                        <span className="hidden sm:inline">
                            {classId && lectureId ? 'Back to Lecture' : 'Back to Chat Home'}
                        </span>
                    </button>
                    <h1 className="text-lg sm:text-xl font-semibold truncate flex-1" style={{ color: theme.text }}>
                        {lecture?.title || threads.find(t => t.id === activeThread)?.title || 'Chat'}
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    {sessionId && (
                        <button
                            onClick={() => {
                                if (window.confirm('Are you sure you want to delete this chat?')) {
                                    deleteChat();
                                }
                            }}
                            className="flex items-center gap-1 sm:gap-2 py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg transition-colors"
                            style={{ color: theme.accent }}
                            title="Delete chat"
                        >
                            <Trash2 size={18} />
                            <span className="hidden sm:inline">Delete Chat</span>
                        </button>
                    )}
                    {isMobile && threads.length > 0 && (
                        <button
                            onClick={toggleSidebar}
                            className="p-1.5 rounded-md"
                            style={{ color: theme.accent }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="3" y1="12" x2="21" y2="12"></line>
                                <line x1="3" y1="6" x2="21" y2="6"></line>
                                <line x1="3" y1="18" x2="21" y2="18"></line>
                            </svg>
                        </button>
                    )}
                </div>
            </header>

            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar */}
                <div 
                    className={`h-[calc(100vh-56px)] z-20 border-r ${isMobile ? 'fixed transform transition-transform duration-200 w-[85%]' : 'w-[300px] relative'} ${isMobile && showSidebar ? 'translate-x-0' : isMobile ? '-translate-x-full' : ''}`}
                    style={{ 
                        borderColor: theme.border, 
                        background: theme.background,
                        boxShadow: isMobile ? '0 0 15px rgba(0,0,0,0.1)' : 'none'
                    }}
                >
                    <div className="mb-5 p-5 pb-5" style={{ borderBottom: `1px solid ${theme.border}` }}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-base font-medium" style={{ color: theme.text }}>Conversations</h3>
                            {isMobile && (
                                <button
                                    onClick={toggleSidebar}
                                    className="p-1 rounded-md"
                                    style={{ color: theme.accent }}
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </div>
                        <button 
                            onClick={createNewThread}
                            className="w-full py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 hover:transform hover:-translate-y-0.5 font-medium text-sm"
                            style={{ 
                                background: theme.primary,
                                color: theme.text
                            }}
                        >
                            <Plus size={16} />
                            New Chat
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-5">
                        {threads.map((thread) => (
                            <div
                                key={thread.id}
                                onClick={() => handleThreadSelect(thread.id)}
                                className="py-3.5 px-4 rounded-lg mb-2 cursor-pointer transition-all duration-200 flex justify-between items-center group hover:transform hover:-translate-y-0.5"
                                style={{
                                    background: activeThread === thread.id ? theme.secondary : 'transparent',
                                    borderColor: activeThread === thread.id ? theme.border : 'transparent'
                                }}
                            >
                                <div className="flex-1 min-w-0 mr-2">
                                    <span className="block font-medium text-sm mb-1"
                                         style={{ color: theme.text }}
                                    >
                                        {thread.title}
                                    </span>
                                    <p className="text-xs truncate"
                                       style={{ color: theme.accent }}
                                    >
                                        {thread.messages[thread.messages.length - 1]?.content.substring(0, 40) || 'New conversation'}...
                                    </p>
                                </div>
                                {thread.id !== 'default' && (
                                    <button 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteThread(thread.id);
                                        }}
                                        className="p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                        style={{ color: theme.accent }}
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {isMobile && showSidebar && (
                    <div 
                        className="fixed inset-0 bg-black bg-opacity-30 z-10"
                        onClick={toggleSidebar}
                    />
                )}

                {/* Main chat area with flexbox to allow for fixed-position behavior */}
                <div className="flex-1 flex flex-col h-full overflow-hidden" style={{ background: theme.background }}>
                    {/* Thread header - sticky */}
                    <div className="border-b p-3 sm:p-4 z-10 sticky top-0" 
                        style={{ 
                            borderColor: theme.border, 
                            background: theme.background
                        }}
                    >
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg sm:text-xl font-semibold truncate flex-1" style={{ color: theme.text }}>
                                {threads.find(t => t.id === activeThread)?.title || 'New Chat'}
                            </h2>
                            {chatContext && (
                                <div className="ml-2 text-xs px-2 py-1 rounded-full" style={{ background: theme.secondary, color: theme.accent }}>
                                    {chatContext.type === 'lecture' && chatContext.lecture?.title ? 
                                        chatContext.lecture.title :
                                        chatContext.type === 'lectures' ? 
                                            `${chatContext.lectures?.length || 0} Lectures` : 
                                            chatContext.type === 'class' && chatContext.class?.title ?
                                                chatContext.class.title :
                                                'Class Chat'
                                    }
                                </div>
                            )}
                            <div className="flex items-center gap-2">
                                <button 
                                    className="flex items-center gap-1 sm:gap-2 py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg transition-colors"
                                    onClick={() => setShowPrompts(!showPrompts)}
                                    style={{ color: theme.accent }}
                                >
                                    <Sparkles size={16} />
                                    <span className="hidden sm:inline">Suggestions</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Layout structure with flex to ensure proper scroll behavior */}
                    <div className="flex-1 flex flex-col h-full overflow-hidden">
                        {/* Scrollable content with padding at the bottom for input area */}
                        <div 
                            className="flex-1 overflow-y-auto"
                            ref={chatContainerRef}
                            style={{ background: theme.background }}
                            onScroll={handleChatScroll}
                        >
                            <div className="px-2 sm:px-4 py-4 sm:py-6 pb-32 sm:pb-40">
                                {threads.find(t => t.id === activeThread)?.messages.length === 0 ? (
                                    <div className="flex items-center justify-center h-full min-h-[70vh]">
                                        <div className="max-w-[1000px] w-full p-3 sm:p-8 text-center">
                                            <h2 className="text-2xl sm:text-4xl font-bold mb-6 sm:mb-14 leading-tight" style={{ color: theme.text }}>
                                                {chatContext?.type === 'lecture' 
                                                    ? "How can I help you understand this lecture?" 
                                                    : chatContext?.type === 'lectures'
                                                    ? `How can I help you with these ${chatContext.lectures?.length || 0} lectures?`
                                                    : chatContext?.type === 'class'
                                                    ? `How can I help you understand this class?`
                                                    : "How can I help you today?"}
                                            </h2>
                                            {showPrompts && (
                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-7">
                                                    {suggestedPrompts.map((prompt, index) => (
                                                        <PromptCard 
                                                            key={index}
                                                            prompt={prompt}
                                                            onClick={() => handlePromptClick(prompt.message)}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-4 sm:gap-6 max-w-4xl mx-auto">
                                        {threads.find(t => t.id === activeThread)?.messages.map((message, index) => {
                                            const messageId = `${activeThread}-${index}`;
                                            const isComplete = completedMessages[messageId] || message.role === 'user';
                                            
                                            return (
                                                <div key={index} className={`group ${message.role === 'user' ? 'flex justify-end' : 'flex justify-start'}`}>
                                                    <div 
                                                        className={`${message.role === 'user' ? 'ml-2 sm:ml-8 md:ml-16' : 'mr-2 sm:mr-8 md:mr-16'} w-fit max-w-[95%] sm:max-w-[90%]`}
                                                    >
                                                        <div
                                                            className="px-3 sm:px-5 py-2.5 sm:py-3.5 rounded-xl border"
                                                            style={{ 
                                                                background: message.role === 'assistant' ? theme.secondary : theme.primary,
                                                                borderColor: theme.border,
                                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                                                            }}
                                                        >
                                                            {message.images && message.images.length > 0 && (
                                                                <div className="mb-3 flex flex-wrap gap-2">
                                                                    {message.images.map((img, imgIndex) => (
                                                                        <div key={imgIndex} className="rounded-lg overflow-hidden border" style={{ borderColor: theme.border }}>
                                                                            <img 
                                                                                src={img} 
                                                                                alt={`Attachment ${imgIndex + 1}`} 
                                                                                className="max-h-36 sm:max-h-48 max-w-full object-contain"
                                                                            />
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {message.role === 'assistant' ? (
                                                                isThinking && threads.find(t => t.id === activeThread)?.messages.indexOf(message) === threads.find(t => t.id === activeThread)?.messages.length - 1 ? (
                                                                    <div style={{ color: theme.text }} className="animate-pulse">
                                                                        <div className="flex items-center gap-2">
                                                                            <Loader2 className="animate-spin" size={16} />
                                                                            <span style={{ color: theme.text }}>Thinking...</span>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    completedMessages[messageId] ? (
                                                                        <div style={{ color: theme.text }}>
                                                                            <ReactMarkdown
                                                                                remarkPlugins={[remarkMath]}
                                                                                rehypePlugins={[rehypeKatex]}
                                                                                className="prose max-w-none prose-sm sm:prose-base"
                                                                                components={{
                                                                                    p: ({children, ...props}) => <p style={{color: theme.text}} className="mb-4 last:mb-0 text-sm sm:text-base" {...props}>{children}</p>,
                                                                                    h1: ({children, ...props}) => <h1 style={{color: theme.text}} className="text-lg sm:text-xl mb-2" {...props}>{children}</h1>,
                                                                                    h2: ({children, ...props}) => <h2 style={{color: theme.text}} className="text-base sm:text-lg mb-2" {...props}>{children}</h2>,
                                                                                    h3: ({children, ...props}) => <h3 style={{color: theme.text}} className="text-base mb-2" {...props}>{children}</h3>,
                                                                                    li: ({children, ...props}) => <li style={{color: theme.text}} className="text-sm sm:text-base" {...props}>{children}</li>,
                                                                                    strong: ({children, ...props}) => <strong style={{color: theme.text}} {...props}>{children}</strong>,
                                                                                    em: ({children, ...props}) => <em style={{color: theme.text}} {...props}>{children}</em>,
                                                                                    code: ({children, ...props}) => <code style={{
                                                                                        backgroundColor: 'rgba(0,0,0,0.05)', 
                                                                                        padding: '2px 4px', 
                                                                                        borderRadius: '3px', 
                                                                                        color: theme.text
                                                                                    }} {...props}>{children}</code>
                                                                                }}
                                                                            >
                                                                                {message.content}
                                                                            </ReactMarkdown>
                                                                        </div>
                                                                    ) : (
                                                                        index === threads.find(t => t.id === activeThread)?.messages.length - 1 ? (
                                                                            <TypewriterText 
                                                                                key={TypewriterKey.current[messageId] || messageId}
                                                                                content={message.content} 
                                                                                onComplete={() => handleMessageComplete(messageId)}
                                                                            />
                                                                        ) : (
                                                                            <div style={{ color: theme.text }}>
                                                                                <ReactMarkdown
                                                                                    remarkPlugins={[remarkMath]}
                                                                                    rehypePlugins={[rehypeKatex]}
                                                                                    className="prose max-w-none prose-sm sm:prose-base"
                                                                                    components={{
                                                                                        p: ({children, ...props}) => <p style={{color: theme.text}} className="mb-4 last:mb-0 text-sm sm:text-base" {...props}>{children}</p>,
                                                                                        h1: ({children, ...props}) => <h1 style={{color: theme.text}} className="text-lg sm:text-xl mb-2" {...props}>{children}</h1>,
                                                                                        h2: ({children, ...props}) => <h2 style={{color: theme.text}} className="text-base sm:text-lg mb-2" {...props}>{children}</h2>,
                                                                                        h3: ({children, ...props}) => <h3 style={{color: theme.text}} className="text-base mb-2" {...props}>{children}</h3>,
                                                                                        li: ({children, ...props}) => <li style={{color: theme.text}} className="text-sm sm:text-base" {...props}>{children}</li>,
                                                                                        strong: ({children, ...props}) => <strong style={{color: theme.text}} {...props}>{children}</strong>,
                                                                                        em: ({children, ...props}) => <em style={{color: theme.text}} {...props}>{children}</em>,
                                                                                        code: ({children, ...props}) => <code style={{
                                                                                            backgroundColor: 'rgba(0,0,0,0.05)', 
                                                                                            padding: '2px 4px', 
                                                                                            borderRadius: '3px', 
                                                                                            color: theme.text
                                                                                        }} {...props}>{children}</code>
                                                                                    }}
                                                                                >
                                                                                    {message.content}
                                                                                </ReactMarkdown>
                                                                            </div>
                                                                        )
                                                                    )
                                                                )
                                                            ) : (
                                                                <div style={{ color: theme.text }}>
                                                                    <ReactMarkdown
                                                                        remarkPlugins={[remarkMath]}
                                                                        rehypePlugins={[rehypeKatex]}
                                                                        className="prose max-w-none prose-sm sm:prose-base"
                                                                        components={{
                                                                            p: ({children, ...props}) => <p style={{color: theme.text}} className="mb-4 last:mb-0 text-sm sm:text-base" {...props}>{children}</p>,
                                                                            h1: ({children, ...props}) => <h1 style={{color: theme.text}} className="text-lg sm:text-xl mb-2" {...props}>{children}</h1>,
                                                                            h2: ({children, ...props}) => <h2 style={{color: theme.text}} className="text-base sm:text-lg mb-2" {...props}>{children}</h2>,
                                                                            h3: ({children, ...props}) => <h3 style={{color: theme.text}} className="text-base mb-2" {...props}>{children}</h3>,
                                                                            li: ({children, ...props}) => <li style={{color: theme.text}} className="text-sm sm:text-base" {...props}>{children}</li>,
                                                                            strong: ({children, ...props}) => <strong style={{color: theme.text}} {...props}>{children}</strong>,
                                                                            em: ({children, ...props}) => <em style={{color: theme.text}} {...props}>{children}</em>,
                                                                            code: ({children, ...props}) => <code style={{
                                                                                backgroundColor: 'rgba(0,0,0,0.05)', 
                                                                                padding: '2px 4px', 
                                                                                borderRadius: '3px', 
                                                                                color: theme.text
                                                                            }} {...props}>{children}</code>
                                                                        }}
                                                                    >
                                                                        {message.content}
                                                                    </ReactMarkdown>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {isComplete && (
                                                            <MessageActions 
                                                                message={message} 
                                                                regenerateResponse={regenerateResponse}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        
                                        {contextSuggestions.length > 0 && !isThinking && (
                                            <div className="flex justify-center">
                                                <ContextSuggestions 
                                                    suggestions={contextSuggestions} 
                                                    onSuggestionClick={handleSuggestionClick}
                                                    theme={theme}
                                                />
                                            </div>
                                        )}
                                        
                                        {isThinking && (
                                            <div className="flex justify-start">
                                                <div className="mr-2 sm:mr-8 md:mr-16 w-fit">
                                                    <div 
                                                        className="flex items-center gap-3 py-2 sm:py-3 px-3 sm:px-5 rounded-xl border"
                                                        style={{ 
                                                            background: theme.secondary, 
                                                            borderColor: theme.border,
                                                            color: theme.accent,
                                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                                                        }}
                                                    >
                                                        <Loader2
                                                            size={18}
                                                            className="animate-spin"
                                                            style={{ color: theme.accent }}
                                                        />
                                                        <span className="text-sm">Thinking...</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Scroll to bottom button */}
                        {showScrollButton && (
                            <button
                                onClick={scrollToBottom}
                                className="fixed bottom-24 right-4 sm:right-8 z-20 p-2 rounded-full shadow-lg transition-all hover:transform hover:scale-110"
                                style={{ 
                                    background: theme.primary,
                                    color: theme.text,
                                    borderColor: theme.border,
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                }}
                                aria-label="Scroll to bottom"
                            >
                                <ChevronsDown size={24} />
                            </button>
                        )}

                        {/* Input area - sticky at the bottom */}
                        <div 
                            className="border-t p-2 sm:p-5 sticky bottom-0 w-full z-10"
                            style={{ 
                                borderColor: theme.border, 
                                background: theme.background,
                                boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)'
                            }}
                        >
                            <form onSubmit={handleSubmit} className="max-w-[900px] mx-auto">
                                {imageFiles.length > 0 && (
                                    <div className="mb-3 flex gap-2 flex-wrap">
                                        {compressedImageData.map((imgData, index) => (
                                            <ImagePreview
                                                key={index}
                                                imageFile={imgData.file}
                                                size={imgData.compressedSize}
                                                onRemove={() => removeImageAttachment(index)}
                                                theme={theme}
                                            />
                                        ))}
                                    </div>
                                )}
                                
                                <div className="flex items-end gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl border"
                                    style={{ background: theme.secondary, borderColor: theme.border }}
                                >
                                    <textarea
                                        ref={textareaRef}
                                        value={input}
                                        onChange={(e) => {
                                            setInput(e.target.value);
                                            adjustTextareaHeight();
                                        }}
                                        onKeyDown={handleKeyDown}
                                        placeholder={isProcessingImages ? "Processing images..." : "Ask about the lecture..."}
                                        rows="1"
                                        disabled={isProcessingImages || isThinking}
                                        className="flex-1 bg-transparent border-none p-1 sm:p-2 min-h-[24px] max-h-[80px] sm:max-h-[150px] text-sm sm:text-base resize-none focus:outline-none disabled:opacity-50"
                                        style={{ color: theme.text }}
                                    />
                                    <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                                        <input
                                            type="file"
                                            ref={imageInputRef}
                                            onChange={handleImageAttach}
                                            accept="image/jpeg,image/png,image/webp"
                                            multiple
                                            className="hidden"
                                            disabled={isProcessingImages || isThinking}
                                        />
                                        <button 
                                            type="button"
                                            onClick={() => imageInputRef.current?.click()}
                                            className="p-1.5 sm:p-2 rounded-lg transition-colors disabled:opacity-50"
                                            style={{ color: theme.accent }}
                                            disabled={isProcessingImages || isThinking}
                                        >
                                            <ImageIcon size={18} />
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={startVoiceInput}
                                            className={`p-1.5 sm:p-2 rounded-lg transition-colors ${isRecording ? 'animate-pulse text-red-500' : ''} disabled:opacity-50`}
                                            style={{ color: theme.accent }}
                                            disabled={isProcessingImages || isThinking}
                                        >
                                            <Mic size={18} />
                                        </button>
                                        <button 
                                            type="submit" 
                                            disabled={(!input.trim() && imageFiles.length === 0) || isProcessingImages || isThinking}
                                            className="p-1.5 sm:p-2 rounded-lg transition-colors disabled:opacity-50"
                                            style={{ 
                                                background: theme.primary,
                                                color: theme.text
                                            }}
                                        >
                                            {isProcessingImages ? (
                                                <Loader2
                                                    size={16}
                                                    className="animate-spin"
                                                    style={{ color: theme.text }}
                                                />
                                            ) : (
                                                <Send size={18} />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LectureChat;
